import { NotificationState } from './state/notification.state';
// ************************************************************
//
//    ANGULAR 2 PLATFORM
//
// ************************************************************
import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';

// ************************************************************
//
//    ANGULAR MATERIAL
//
// ************************************************************
import { MaterialModule } from './shared/material.module';

// ************************************************************
//
//    MAIN LAYER ROOT COMPONENTS
//
// ************************************************************
import { AppComponent } from './app.component';
import { ToolbarComponent } from './toolbar/toolbar.component';
import { LanguageComponent } from './toolbar/language/language.component';
import { AboutComponent } from './toolbar/about/about.component';
import { SidenavComponent } from './sidenav/sidenav.component';

// ************************************************************
//
//    ANGULAR LOADER
//
// ************************************************************
import { LoaderModule } from './loader/loader.module';


// ************************************************************
//
//    ANGULAR l10n
//
// ************************************************************
import {
  L10nConfig,
  L10nLoader,
  LocalizationModule,
  LocaleValidationModule,
  StorageStrategy,
  ProviderType,
  TranslationModule,
  ISOCode,
  TranslationService
} from 'angular-l10n';
import { NotificationService } from './services/common/services-notifications';
import { AuthService } from './services/common/services-auth';
import { TranslationSharedModule } from './shared/translation.module';
import { HttpService } from './services/markets-report/services-http';
import { AppBarPortalsService } from './services/common/app-bar-portals-service.module';
import { LazyTranslationService } from './services/common/lazy-translation-service.module';
import { CrumbsService } from './services/common/services-crumbs';
import { TokenInterceptor } from './services/common/token-interceptor';
import {NgxsModule, Store} from '@ngxs/store';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsRouterPluginModule, RouterStateSerializer } from '@ngxs/router-plugin';
import { AppState } from './state/app.state';
import { AuthState } from './state/auth.state';
import { CustomRouterStateSerializer } from './state/router.state-serializer';

import { PushNotificationService } from './services/common/services-push-notifications';
import { ManageLocationsState } from './state/manage-locations.state';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import {KeycloakAngularModule, KeycloakService} from "keycloak-angular";
import {initializeKeycloak} from "./init/keycloak-init.factory";

const l10nConfig: L10nConfig = {
  locale: {
      languages: [
          { code: 'en', dir: 'ltr' },
          { code: 'it', dir: 'ltr' },
          { code: 'de', dir: 'ltr' },
          { code: 'ar', dir: 'rtl' },
          { code: 'id', dir: 'ltr' },
          { code: 'hi', dir: 'ltr' }
      ],
      defaultLocale: { languageCode: 'en', countryCode: 'AU' },
      currency: 'USD',
      storage: StorageStrategy.Cookie,
      cookieExpiration: 30
  },
  translation: {
      providers: [
          // tslint:disable-next-line:max-line-length
          { type: 'incremental', prefix: 'https://h1gouahti6.execute-api.ap-southeast-2.amazonaws.com/staging/languages/translate', incrementalFallbackLanguage: [ISOCode.Language]},
          { type: 'incremental', prefix: 'https://h1gouahti6.execute-api.ap-southeast-2.amazonaws.com/staging/languages/translate'},
          { type: ProviderType.Fallback, prefix: './assets/locale-', fallbackLanguage: [ISOCode.Language] },
          { type: ProviderType.Static, prefix: './assets/locale-' }
      ],
      caching: true,
      composedLanguage: [ISOCode.Language, ISOCode.Country],
      composedKeySeparator: '.',
      missingValue: 'No key',
      i18nPlural: true
  }
};

// Advanced initialization.
export function initL10n(l10nLoader: L10nLoader): Function {
  return () => l10nLoader.load();
}

@NgModule({
    declarations: [
        AppComponent,
        ToolbarComponent,
        SidenavComponent,
        AboutComponent,
        LanguageComponent,
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        MaterialModule,
        LoaderModule,
        HttpClientModule,
        TranslationSharedModule,
        TranslationModule.forRoot(l10nConfig),
        NgxsModule.forRoot([AppState, AuthState, NotificationState, ManageLocationsState], { developmentMode: !environment.production }),
        NgxsStoragePluginModule.forRoot({ key: ['push_notifications', 'manageLocations'] }),
        NgxsRouterPluginModule.forRoot(),
        NgxsLoggerPluginModule.forRoot(),
        NgxsReduxDevtoolsPluginModule.forRoot(),
        ServiceWorkerModule.register('/service-worker.js', { enabled: environment.production }),
        KeycloakAngularModule,
    ],
    providers: [
        {
            provide: APP_INITIALIZER,
            useFactory: initializeKeycloak,
            deps: [KeycloakService, Store, NotificationService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initL10n,
            deps: [L10nLoader],
            multi: true
        },
        NotificationService,
        PushNotificationService,
        AuthService,
        HttpService,
        CrumbsService,
        AppBarPortalsService,
        { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
        { provide: TranslationService, useClass: LazyTranslationService },
        { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
  constructor(public l10nLoader: L10nLoader) {
    this.l10nLoader.load();
  }
}
